// M13.2 — pure-string dual-axis SVG builder for the Depth PDF page.
//
// Mirrors `DepthChart.tsx` from the dashboard but emits a flat
// `<svg>...</svg>` string with no DOM, no React, no event handlers — safe
// to call from inside a Cloudflare Worker where DocRaptor's static-HTML
// pipeline picks it up.
//
// Pageviews on the LEFT axis (filled blurple area + line). Active users
// on the RIGHT axis (dashed purple line). Independent linear scales so
// the gap between consumption + reach is legible regardless of the
// absolute magnitudes.

import { extent, max as d3Max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { area as d3Area, line as d3Line } from "d3-shape";

export interface DepthSvgPoint {
  date: string;
  pageviews: number;
  activeUsers: number;
}

export interface BuildDepthSvgOpts {
  width?: number;
  height?: number;
  granularity: "day" | "week" | "month";
}

const DEFAULT_WIDTH = 680;
const DEFAULT_HEIGHT = 240;
const MARGIN = { top: 28, right: 52, bottom: 28, left: 52 };
const AXIS_COLOR = "#6B7280";
const GRID_COLOR = "#E5E7EB";
const PAGEVIEW_COLOR = "#4F31FF";
const PAGEVIEW_FILL = "rgba(79, 49, 255, 0.12)";
const USERS_COLOR = "#9583FF";

/**
 * Build a static dual-axis SVG line chart for the PDF. Returns the
 * inline `<svg>...</svg>` string, ready to drop into the print-html
 * template.
 */
export function buildDepthSvg(rows: DepthSvgPoint[], opts: BuildDepthSvgOpts): string {
  const width = opts.width ?? DEFAULT_WIDTH;
  const height = opts.height ?? DEFAULT_HEIGHT;
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;

  if (rows.length === 0) {
    return emptyChart(width, height);
  }

  const dateObjs = rows.map((r) => ({ ...r, dateObj: new Date(`${r.date}T00:00:00Z`) }));
  const [xMin, xMax] = extent(dateObjs, (d) => d.dateObj) as [Date, Date];
  const pageviewMaxRaw = d3Max(dateObjs, (d) => d.pageviews) ?? 0;
  const usersMaxRaw = d3Max(dateObjs, (d) => d.activeUsers) ?? 0;
  const pageviewMax = pageviewMaxRaw === 0 ? 1 : pageviewMaxRaw * 1.1;
  const usersMax = usersMaxRaw === 0 ? 1 : usersMaxRaw * 1.1;

  const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
  const yLeft = scaleLinear().domain([0, pageviewMax]).range([innerH, 0]);
  const yRight = scaleLinear().domain([0, usersMax]).range([innerH, 0]);

  const xTickCount = opts.granularity === "day" ? 6 : opts.granularity === "week" ? 5 : 4;
  const xTicks = xScale.ticks(xTickCount);
  const yLeftTicks = yLeft.ticks(4);
  const yRightTicks = yRight.ticks(4);

  const areaGen = d3Area<{ dateObj: Date; pageviews: number }>()
    .x((d) => xScale(d.dateObj))
    .y0(innerH)
    .y1((d) => yLeft(d.pageviews));
  const lineGenLeft = d3Line<{ dateObj: Date; pageviews: number }>()
    .x((d) => xScale(d.dateObj))
    .y((d) => yLeft(d.pageviews));
  const lineGenRight = d3Line<{ dateObj: Date; activeUsers: number }>()
    .x((d) => xScale(d.dateObj))
    .y((d) => yRight(d.activeUsers));

  const pageviewArea = areaGen(dateObjs) ?? "";
  const pageviewLine = lineGenLeft(dateObjs) ?? "";
  const usersLine = lineGenRight(dateObjs) ?? "";

  const gridlines = yLeftTicks
    .map((v) => {
      const y = yLeft(v);
      return (
        `<line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="${GRID_COLOR}" stroke-width="1" />` +
        `<text x="-8" y="${y + 3}" font-size="10" fill="${PAGEVIEW_COLOR}" text-anchor="end" font-family="inherit">${escapeText(formatYTick(v))}</text>`
      );
    })
    .join("");

  const rightAxisLabels = yRightTicks
    .map((v) => {
      const y = yRight(v);
      return `<text x="${innerW + 8}" y="${y + 3}" font-size="10" fill="${USERS_COLOR}" text-anchor="start" font-family="inherit">${escapeText(formatYTick(v))}</text>`;
    })
    .join("");

  const xAxisTicks = xTicks
    .map((d) => {
      const x = xScale(d);
      return (
        `<line x1="${x}" y1="${innerH}" x2="${x}" y2="${innerH + 4}" stroke="${AXIS_COLOR}" stroke-width="1" />` +
        `<text x="${x}" y="${innerH + 16}" font-size="10" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">${escapeText(formatXTick(d, opts.granularity))}</text>`
      );
    })
    .join("");

  const pageviewArtwork =
    `<path d="${pageviewArea}" fill="${PAGEVIEW_FILL}" />` +
    `<path d="${pageviewLine}" fill="none" stroke="${PAGEVIEW_COLOR}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />`;
  const usersArtwork = `<path d="${usersLine}" fill="none" stroke="${USERS_COLOR}" stroke-width="2" stroke-dasharray="5 3" stroke-linecap="round" stroke-linejoin="round" />`;

  const yLabelLeft = `<text x="${-MARGIN.left + 4}" y="${-12}" font-size="9" font-weight="700" letter-spacing="1" fill="${PAGEVIEW_COLOR}" font-family="inherit">PAGEVIEWS</text>`;
  const yLabelRight = `<text x="${innerW + MARGIN.right - 4}" y="${-12}" font-size="9" font-weight="700" letter-spacing="1" fill="${USERS_COLOR}" text-anchor="end" font-family="inherit">ACTIVE USERS</text>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Depth — pageviews vs active users">` +
    `<g transform="translate(${MARGIN.left}, ${MARGIN.top})">` +
    yLabelLeft +
    yLabelRight +
    gridlines +
    rightAxisLabels +
    xAxisTicks +
    pageviewArtwork +
    usersArtwork +
    `</g>` +
    `</svg>`
  );
}

function emptyChart(width: number, height: number): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Depth chart (empty)">` +
    `<rect x="0" y="0" width="${width}" height="${height}" fill="#FAFAFA"></rect>` +
    `<text x="${width / 2}" y="${height / 2}" font-size="12" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">No depth data in window</text>` +
    `</svg>`
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function formatYTick(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1_000).toFixed(0)}K`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
